import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { BookOpen, ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { PDF_URL, LEAFLET_URL } from "./data";

const PAGES = [
  { src: "/brochure-page-1.jpg", alt: "The People's Josh workshops booklet, cover" },
  { src: "/brochure-page-2.jpg", alt: "The People's Josh workshops booklet, services spread" },
  { src: "/brochure-page-3.jpg", alt: "The People's Josh workshops booklet, contact page" },
];

const ease = [0.16, 1, 0.3, 1];

export const openBooklet = () => {
  window.dispatchEvent(new CustomEvent("tpj-open-booklet"));
};

export const BookletFlipbook = () => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(1);

  const turn = useCallback((delta) => {
    setDirection(delta > 0 ? 1 : -1);
    setPage((current) => Math.max(0, Math.min(PAGES.length - 1, current + delta)));
  }, []);

  useEffect(() => {
    const onOpen = () => {
      setPage(0);
      setDirection(1);
      setOpen(true);
    };
    window.addEventListener("tpj-open-booklet", onOpen);
    return () => window.removeEventListener("tpj-open-booklet", onOpen);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key === "ArrowRight") turn(1);
      if (event.key === "ArrowLeft") turn(-1);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, turn]);

  const isLastPage = page === PAGES.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-ink/85 p-4 md:p-8 backdrop-blur-md"
          onClick={() => setOpen(false)}
          data-testid="booklet-flipbook"
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Interactive workshops booklet"
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.45, ease }}
            className="relative w-[min(1120px,96vw)] max-h-[94vh] overflow-y-auto bg-bone border border-bone/20 shadow-[0_32px_90px_rgba(0,0,0,0.42)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 px-4 md:px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-pine flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-terra" />
                </span>
                <div>
                  <p className="font-serif text-xl md:text-2xl leading-tight">Workshops Booklet</p>
                  <p className="text-xs text-ink/55">Click the page, use the arrows, or press ← → to flip.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booklet-pdf-link"
                  className="inline-flex items-center gap-2 border border-ink/20 px-3.5 py-2 text-xs font-semibold tracking-wide hover:border-terra hover:text-terra transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open PDF
                </a>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="booklet-close"
                  aria-label="Close workshops booklet"
                  className="w-10 h-10 flex items-center justify-center bg-ink text-bone hover:bg-terra transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-3 md:p-6 bg-sand/50">
              <div className="relative mx-auto max-w-[1040px]" style={{ perspective: 1800 }}>
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.button
                    key={page}
                    type="button"
                    onClick={() => turn(isLastPage ? -1 : 1)}
                    initial={{ opacity: 0, rotateY: direction > 0 ? -72 : 72, x: direction > 0 ? 90 : -90 }}
                    animate={{ opacity: 1, rotateY: 0, x: 0 }}
                    exit={{ opacity: 0, rotateY: direction > 0 ? 72 : -72, x: direction > 0 ? -90 : 90 }}
                    transition={{ duration: 0.48, ease }}
                    style={{ transformStyle: "preserve-3d", transformOrigin: direction > 0 ? "left center" : "right center" }}
                    className="relative flex w-full h-[58vh] md:h-[68vh] max-h-[720px] items-center justify-center overflow-hidden bg-white border border-ink/10 shadow-[18px_24px_60px_rgba(20,40,61,0.22)]"
                    data-testid="booklet-page"
                    data-page={page + 1}
                    aria-label={isLastPage ? "Flip to the previous booklet page" : "Flip to the next booklet page"}
                  >
                    <img src={PAGES[page].src} alt={PAGES[page].alt} className="max-h-full max-w-full object-contain" draggable="false" />
                    <span className="absolute right-3 bottom-3 bg-pine/90 text-bone px-3 py-1.5 text-[0.68rem] tracking-[0.16em] uppercase">
                      {isLastPage ? "Click to go back" : "Click to flip"}
                    </span>
                  </motion.button>
                </AnimatePresence>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => turn(-1)}
                  disabled={page === 0}
                  data-testid="booklet-prev"
                  className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm font-semibold disabled:opacity-35 disabled:cursor-not-allowed hover:border-terra hover:text-terra transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <div className="flex items-center gap-3" data-testid="booklet-status" aria-live="polite">
                  {PAGES.map((item, index) => (
                    <button
                      key={item.src}
                      type="button"
                      onClick={() => turn(index - page)}
                      aria-label={`Go to booklet page ${index + 1}`}
                      className={`h-2.5 transition-all duration-300 ${index === page ? "w-8 bg-terra" : "w-2.5 bg-ink/20 hover:bg-ink/40"}`}
                    />
                  ))}
                  <span className="text-xs font-semibold text-ink/55">Page {page + 1} of {PAGES.length}</span>
                </div>
                <button
                  type="button"
                  onClick={() => turn(1)}
                  disabled={isLastPage}
                  data-testid="booklet-next"
                  className="inline-flex items-center gap-2 bg-terra text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-35 disabled:cursor-not-allowed hover:bg-ink transition-colors"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-5 border-t border-ink/10 pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm text-ink/60">Looking for strategic whole-school inclusion support?</p>
                <a
                  href={LEAFLET_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booklet-leaflet-link"
                  className="inline-flex items-center justify-center gap-2 border border-ink/20 px-4 py-2.5 text-sm font-semibold text-ink hover:border-terra hover:text-terra transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> Read the Inclusion Support Leaflet
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
